import { Grid } from '@material-ui/core';
import { ButtonLink } from '@backstage/ui';
import {
  EntityApiDefinitionCard,
  EntityConsumedApisCard,
  EntityConsumingComponentsCard,
  EntityHasApisCard,
  EntityProvidedApisCard,
  EntityProvidingComponentsCard,
} from '@backstage/plugin-api-docs';
import {
  EntityAboutCard,
  EntityDependsOnComponentsCard,
  EntityDependsOnResourcesCard,
  EntityHasComponentsCard,
  EntityHasResourcesCard,
  EntityHasSubcomponentsCard,
  EntityHasSystemsCard,
  EntityLayout,
  EntityLinksCard,
  EntitySwitch,
  EntityOrphanWarning,
  EntityProcessingErrorsPanel,
  isComponentType,
  isKind,
  hasCatalogProcessingErrors,
  isOrphan,
} from '@backstage/plugin-catalog';
import {
  EntityOwnershipCard,
  EntityUserProfileCard,
  EntityGroupProfileCard,
  EntityMembersListCard,
} from '@backstage/plugin-org';
import {
  TechDocsReaderPage,
  TechDocsReaderPageSubheader,
} from '@backstage/plugin-techdocs';
import { EmptyState } from '@backstage/core-components';
import {
  Direction,
  EntityCatalogGraphCard,
} from '@backstage/plugin-catalog-graph';
import {
  GroupEntity,
  getCompoundEntityRef,
  parseEntityRef,
  RELATION_API_CONSUMED_BY,
  RELATION_API_PROVIDED_BY,
  RELATION_CONSUMES_API,
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
  RELATION_HAS_PART,
  RELATION_PART_OF,
  RELATION_PROVIDES_API,
} from '@backstage/catalog-model';
import {
  MissingAnnotationEmptyState,
  useEntity,
} from '@backstage/plugin-catalog-react';
import {
  TECHDOCS_ANNOTATION,
  TECHDOCS_EXTERNAL_ANNOTATION,
  TECHDOCS_EXTERNAL_PATH_ANNOTATION,
} from '@backstage/plugin-techdocs-common';

import { Mermaid } from 'backstage-plugin-techdocs-addon-mermaid';
import { icons as logos } from '@iconify-json/logos';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { TechDocsEditButton } from '../techdocs/TechDocsEditButton';
import { TechDocsReaderPageContentWithFeedback } from '../techdocs/TechDocsFeedbackAddon';

import {
  isGitlabAvailable,
  EntityGitlabCoverageCard,
  EntityGitlabIssuesTable,
  EntityGitlabLanguageCard,
  EntityGitlabMergeRequestsTable,
  EntityGitlabMergeRequestStatsCard,
  EntityGitlabPeopleCard,
  EntityGitlabPipelinesTable,
  EntityGitlabReadmeCard,
  EntityGitlabReleasesCard,
} from '@immobiliarelabs/backstage-plugin-gitlab';

import { GroupInfoCard } from './GroupInfoCard';
import { EntityEcsContent, isEcsAvailable } from '@traton/backstage-plugin-ecs';

const EntityEmbeddedTechdocsContent = () => {
  const { entity } = useEntity();
  const annotations = entity.metadata.annotations ?? {};

  if (
    !annotations[TECHDOCS_ANNOTATION] &&
    !annotations[TECHDOCS_EXTERNAL_ANNOTATION]
  ) {
    return <MissingAnnotationEmptyState annotation={[TECHDOCS_ANNOTATION]} />;
  }

  let entityRef;
  const externalEntityRef = annotations[TECHDOCS_EXTERNAL_ANNOTATION];
  if (externalEntityRef) {
    try {
      entityRef = parseEntityRef(externalEntityRef);
    } catch {
      entityRef = getCompoundEntityRef(entity);
    }
  }

  return (
    <TechDocsReaderPage entityRef={entityRef}>
      <TechDocsReaderPageSubheader />
      <TechDocsReaderPageContentWithFeedback
        defaultPath={annotations[TECHDOCS_EXTERNAL_PATH_ANNOTATION]}
      />
      <TechDocsEditButton />
      <ReportIssue />
      <Mermaid
        iconLoaders={[
          {
            name: logos.prefix,
            icons: logos,
          },
        ]}
      />
    </TechDocsReaderPage>
  );
};

const pipelinesContent = (
  // This is an example of how you can implement your company's logic in entity page.
  // You can for example enforce that all components of type 'service' should use GitHubActions
  <EntitySwitch>
    <EntitySwitch.Case if={isGitlabAvailable}>
      <EntityGitlabPipelinesTable />
    </EntitySwitch.Case>

    <EntitySwitch.Case>
      <EmptyState
        title="No CI/CD available for this entity"
        missing="info"
        description="You need to add an annotation to your component if you want to enable CI/CD for it. You can read more about annotations in Backstage by clicking the button below."
        action={
          <ButtonLink
            variant="primary"
            href="https://backstage.io/docs/features/software-catalog/well-known-annotations"
          >
            Read more
          </ButtonLink>
        }
      />
    </EntitySwitch.Case>
  </EntitySwitch>
);

const gitlabContent = (
  <Grid container spacing={3} alignItems="stretch">
    <Grid item sm={12} md={3} lg={3}>
      <EntityGitlabPeopleCard />
    </Grid>
    <Grid item sm={12} md={3} lg={3}>
      <EntityGitlabLanguageCard />
    </Grid>
    <Grid item sm={12} md={3} lg={3}>
      <EntityGitlabMergeRequestStatsCard />
    </Grid>
    <Grid item sm={12} md={3} lg={3}>
      <EntityGitlabReleasesCard />
    </Grid>
    <Grid item md={12}>
      <EntityGitlabPipelinesTable />
    </Grid>
    <Grid item md={12}>
      <EntityGitlabMergeRequestsTable />
    </Grid>
    <Grid item md={12}>
      <EntityGitlabIssuesTable />
    </Grid>
    <Grid item md={12}>
      <EntityGitlabCoverageCard />
    </Grid>
  </Grid>
);

const gitlabTabs = (
  <>
    <EntityLayout.Route if={isGitlabAvailable} path="/readme" title="README">
      <EntityGitlabReadmeCard />
    </EntityLayout.Route>

    <EntityLayout.Route if={isGitlabAvailable} path="/gitlab" title="Gitlab">
      {gitlabContent}
    </EntityLayout.Route>

    <EntityLayout.Route if={isGitlabAvailable} path="/people" title="People">
      <EntityGitlabPeopleCard />
    </EntityLayout.Route>

    <EntityLayout.Route
      if={isGitlabAvailable}
      path="/languages"
      title="Languages"
    >
      <EntityGitlabLanguageCard />
    </EntityLayout.Route>

    <EntityLayout.Route
      if={isGitlabAvailable}
      path="/merge-requests"
      title="Merge Requests"
    >
      <>
        <EntityGitlabMergeRequestStatsCard />
        <EntityGitlabMergeRequestsTable />
      </>
    </EntityLayout.Route>

    <EntityLayout.Route
      if={isGitlabAvailable}
      path="/releases"
      title="Releases"
    >
      <EntityGitlabReleasesCard />
    </EntityLayout.Route>

    <EntityLayout.Route if={isGitlabAvailable} path="/issues" title="Issues">
      <EntityGitlabIssuesTable />
    </EntityLayout.Route>

    <EntityLayout.Route
      if={isGitlabAvailable}
      path="/pipelines"
      title="Pipelines"
    >
      {pipelinesContent}
    </EntityLayout.Route>
  </>
);

const entityWarningContent = (
  <>
    <EntitySwitch>
      <EntitySwitch.Case if={isOrphan}>
        <Grid item xs={12}>
          <EntityOrphanWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasCatalogProcessingErrors}>
        <Grid item xs={12}>
          <EntityProcessingErrorsPanel />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
  </>
);

const overviewContent = (
  <Grid container spacing={3} alignItems="stretch">
    {entityWarningContent}

    <Grid item md={6}>
      <EntityAboutCard />
    </Grid>
    <Grid item md={6} xs={12}>
      <EntityCatalogGraphCard height={400} />
    </Grid>

    <Grid item md={4} xs={12}>
      <EntityLinksCard />
    </Grid>
    <Grid item md={8} xs={12}>
      <EntityHasSubcomponentsCard />
    </Grid>
  </Grid>
);

const serviceEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/api" title="API">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityProvidedApisCard />
        </Grid>
        <Grid item md={6}>
          <EntityConsumedApisCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/dependencies" title="Dependencies">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityDependsOnComponentsCard />
        </Grid>
        <Grid item md={6}>
          <EntityDependsOnResourcesCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs">
      <EntityEmbeddedTechdocsContent />
    </EntityLayout.Route>

    <EntityLayout.Route if={isEcsAvailable} path="/ecs" title="ECS">
      <EntityEcsContent />
    </EntityLayout.Route>

    {gitlabTabs}
  </EntityLayout>
);

const websiteEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/dependencies" title="Dependencies">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityDependsOnComponentsCard />
        </Grid>
        <Grid item md={6}>
          <EntityDependsOnResourcesCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs">
      <EntityEmbeddedTechdocsContent />
    </EntityLayout.Route>

    {gitlabTabs}
  </EntityLayout>
);

/**
 * NOTE: This page is designed to work on small screens such as mobile devices.
 * This is based on Material UI Grid. If breakpoints are used, each grid item must set the `xs` prop to a column size or to `true`,
 * since this does not default. If no breakpoints are used, the items will equitably share the available space.
 * https://material-ui.com/components/grid/#basic-grid.
 */

const defaultEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs">
      <EntityEmbeddedTechdocsContent />
    </EntityLayout.Route>
  </EntityLayout>
);

const componentPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isComponentType('service')}>
      {serviceEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case if={isComponentType('documentation')}>
      {defaultEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case>{websiteEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);

const apiPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard height={400} />
        </Grid>
        <Grid item md={4} xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid container item md={12}>
          <Grid item md={6}>
            <EntityProvidingComponentsCard />
          </Grid>
          <Grid item md={6}>
            <EntityConsumingComponentsCard />
          </Grid>
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/definition" title="Definition">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <EntityApiDefinitionCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const userPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityUserProfileCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const GroupOverviewContent = () => {
  const { entity } = useEntity();
  const hasOwnership = ({ relations }: GroupEntity) =>
    Boolean(relations?.some(relation => relation.type === 'ownerOf'));
  const hasGroupMembers = ({ spec: { members }, relations }: GroupEntity) =>
    Boolean(
      (Array.isArray(members) && members?.length > 0) ||
        relations?.some(relation => relation.type === 'hasMember'),
    );

  return (
    <Grid container spacing={3}>
      {entityWarningContent}
      <Grid item xs={12} md={6}>
        <EntityGroupProfileCard />
      </Grid>
      <Grid item xs={12} md={6}>
        <GroupInfoCard />
      </Grid>
      {hasOwnership(entity as GroupEntity) && (
        <Grid item xs={12}>
          <EntityOwnershipCard />
        </Grid>
      )}
      {hasGroupMembers(entity as GroupEntity) && (
        <Grid item xs={12}>
          <EntityMembersListCard />
        </Grid>
      )}
    </Grid>
  );
};

const groupPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <GroupOverviewContent />
    </EntityLayout.Route>
  </EntityLayout>
);

const systemPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard height={400} />
        </Grid>
        <Grid item md={4} xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid item md={8}>
          <EntityHasComponentsCard />
        </Grid>
        <Grid item md={6}>
          <EntityHasApisCard />
        </Grid>
        <Grid item md={6}>
          <EntityHasResourcesCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    <EntityLayout.Route path="/diagram" title="Diagram">
      <EntityCatalogGraphCard
        direction={Direction.TOP_BOTTOM}
        title="System Diagram"
        height={700}
        relations={[
          RELATION_PART_OF,
          RELATION_HAS_PART,
          RELATION_API_CONSUMED_BY,
          RELATION_API_PROVIDED_BY,
          RELATION_CONSUMES_API,
          RELATION_PROVIDES_API,
          RELATION_DEPENDENCY_OF,
          RELATION_DEPENDS_ON,
        ]}
        unidirectional={false}
      />
    </EntityLayout.Route>
  </EntityLayout>
);

const domainPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard height={400} />
        </Grid>
        <Grid item md={6}>
          <EntityHasSystemsCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

export const entityPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isKind('component')} children={componentPage} />
    <EntitySwitch.Case if={isKind('api')} children={apiPage} />
    <EntitySwitch.Case if={isKind('group')} children={groupPage} />
    <EntitySwitch.Case if={isKind('user')} children={userPage} />
    <EntitySwitch.Case if={isKind('system')} children={systemPage} />
    <EntitySwitch.Case if={isKind('domain')} children={domainPage} />

    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);
